import { useQueryClient } from "@tanstack/react-query";
import { Loader2, MoreVertical } from "lucide-react";
import { FC, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  getGetTemplateListQueryKey,
  getTemplate,
  useSetDefaultTemplate,
} from "~/api/custom-reports/custom-reports";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader } from "~/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { useOrganizationParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import { DeleteTemplateAlertDialog } from "./DeleteTemplateAlertDialog";
import { EditTemplateModal } from "./EditTemplateModal";
import { prepareTemplateExport } from "./importExportUtils";
import { useDuplicateTemplate } from "./useDuplicateTemplate";
import { ViewTemplateModal } from "./ViewTemplateModal";

interface Template {
  id: string;
  name: string;
  definition: string;
  updatedAt: string;
  createdAt: string;
  status?: "pending" | "processing" | "completed" | "failed";
  isDraft?: boolean;
  isDefault?: boolean;
}

interface TemplatePanelProps {
  template: Template;
  isDefault?: boolean;
  hideMenu?: boolean;
}

export const TemplatePanel: FC<TemplatePanelProps> = ({
  template,
  isDefault = false,
  hideMenu = false,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const intl = useIntl();
  const { organizationId } = useOrganizationParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const duplicateTemplate = useDuplicateTemplate();

  const setDefaultTemplate = useSetDefaultTemplate({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetTemplateListQueryKey(organizationId),
        });
      },
      onError: () => {
        toast({
          title: intl.formatMessage({
            id: "templatePanel.setAsDefault.error",
            defaultMessage: "Failed to set template as default",
          }),
          variant: "destructive",
        });
      },
    },
  });

  const isProcessing =
    template.status === "pending" || template.status === "processing";

  const isFailed = template.status === "failed";

  // Check if template is new (created less than 24 hours ago)
  const isNew = (() => {
    const now = new Date().getTime();
    const created = new Date(template.createdAt).getTime();
    const hoursDiff = (now - created) / (1000 * 60 * 60);
    return hoursDiff < 24;
  })();

  const isUnfinishedDraft = template.isDraft && template.status !== "completed";

  const handleCardClick = () => {
    if (!isProcessing && !isFailed) {
      setIsViewOpen(true);
    }
  };

  const deleteMenuItem = {
    label: intl.formatMessage({
      id: "templatePanel.delete",
      defaultMessage: "Delete",
    }),
    onClick: () => {
      setIsDropdownOpen(false);
      setIsDeleteOpen(true);
    },
    className: "text-destructive",
  };

  const allMenuItems = [
    ...(!isDefault
      ? [
          {
            label: intl.formatMessage({
              id: "templatePanel.setAsDefault",
              defaultMessage: "Set as Default",
            }),
            onClick: () => {
              setIsDropdownOpen(false);
              if (organizationId && template.id) {
                setDefaultTemplate.mutate({
                  organizationId,
                  templateId: template.id,
                });
              }
            },
            disabled: setDefaultTemplate.isPending,
          },
        ]
      : []),
    {
      label: intl.formatMessage({
        id: "templatePanel.edit",
        defaultMessage: "Edit",
      }),
      onClick: () => {
        setIsDropdownOpen(false);
        setIsEditOpen(true);
      },
    },
    {
      label: intl.formatMessage({
        id: "templatePanel.duplicate",
        defaultMessage: "Duplicate",
      }),
      onClick: () => {
        setIsDropdownOpen(false);
        if (organizationId && template.id) {
          duplicateTemplate.mutate({
            organizationId,
            templateId: template.id,
          });
        }
      },
      disabled: duplicateTemplate.isPending,
    },
    {
      label: intl.formatMessage({
        id: "templatePanel.export",
        defaultMessage: "Export",
      }),
      onClick: async () => {
        setIsDropdownOpen(false);
        try {
          if (!organizationId) {
            throw new Error("no-org");
          }
          const data = await getTemplate(organizationId, template.id);
          const payload = prepareTemplateExport({
            name: data.name ?? template.name,
            definition: data.definition ?? template.definition,
            sections: data.sections ?? [],
          });
          const blob = new Blob([JSON.stringify(payload, null, 2)], {
            type: "application/json",
          });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          const safeName =
            (template.name || "template").replace(/[^\w\-]+/g, "_") + ".json";
          a.download = safeName;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
          toast({
            title: intl.formatMessage({
              id: "exportTemplate.success",
              defaultMessage: "Template exported",
            }),
          });
        } catch {
          toast({
            title: intl.formatMessage({
              id: "exportTemplate.error",
              defaultMessage: "Failed to export template",
            }),
          });
        }
      },
    },
    deleteMenuItem,
  ];

  // If status is not "completed", show only Delete option
  const dropdownMenuItems =
    template.status === "completed" ? allMenuItems : [deleteMenuItem];

  return (
    <>
      <Card
        className={`py-[14px] px-[16px] flex flex-col gap-2 shadow-none rounded-[8px] group transition-colors duration-200 ${
          !isUnfinishedDraft ? "cursor-pointer hover:bg-accent/50" : ""
        } ${
          template.isDraft && template.status === "failed"
            ? "border-l-4 border-l-red-500 bg-red-50/30 dark:bg-red-950/20"
            : template.isDraft && template.status !== "completed"
              ? "border-l-4 border-l-gray-400 bg-gray-50/30 dark:bg-gray-950/20"
              : isNew
                ? "border-l-4 border-l-blue-500 bg-blue-50/30 dark:bg-blue-950/20"
                : template.isDraft
                  ? "border-l-4 border-l-blue-500 bg-blue-50/30 dark:bg-blue-950/20"
                  : ""
        }`}
        onClick={!isUnfinishedDraft ? handleCardClick : undefined}
      >
        <CardHeader className="py-0 px-0">
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <h3 className="font-medium text-lg leading-6">{template.name}</h3>
            </div>
            <div className="flex items-center gap-2">
              {isProcessing && (
                <Badge variant="secondary" className="rounded-full">
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  <FormattedMessage
                    id={`templatePanel.status.${template.status}`}
                    defaultMessage={
                      template.status === "pending" ? "Pending" : "Processing"
                    }
                  />
                </Badge>
              )}
              {isFailed && (
                <Badge variant="destructive" className="rounded-full">
                  <FormattedMessage
                    id="templatePanel.status.failed"
                    defaultMessage="Failed"
                  />
                </Badge>
              )}
              {!isProcessing && !isFailed && isNew && (
                <Badge variant="secondary" className="rounded-full">
                  <FormattedMessage
                    id="templatePanel.new"
                    defaultMessage="New"
                  />
                </Badge>
              )}
              {!isProcessing && isDefault && !isNew && (
                <Badge variant="secondary" className="rounded-full">
                  <FormattedMessage
                    id="templatePanel.default"
                    defaultMessage="Default"
                  />
                </Badge>
              )}
              {!hideMenu && (
                <div onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu
                    open={isDropdownOpen}
                    onOpenChange={setIsDropdownOpen}
                  >
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {dropdownMenuItems.map((item) => (
                        <DropdownMenuItem
                          key={item.label}
                          onClick={item.onClick}
                          className={
                            "className" in item ? item.className : undefined
                          }
                          disabled={
                            "disabled" in item ? item.disabled : undefined
                          }
                        >
                          {item.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="py-0 px-0">
          <div className="mb-[16px]">
            <p className="text-sm text-muted-foreground">
              {template.definition}
            </p>
          </div>
        </CardContent>
      </Card>
      <DeleteTemplateAlertDialog
        open={isDeleteOpen}
        setOpen={setIsDeleteOpen}
        templateId={template.id}
        templateName={template.name}
      />
      <EditTemplateModal
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
        organizationId={organizationId}
        templateId={template.id}
      />
      <ViewTemplateModal
        templateId={template.id}
        organizationId={organizationId}
        open={isViewOpen}
        onOpenChange={setIsViewOpen}
        onEdit={() => {
          setIsViewOpen(false);
          setIsEditOpen(true);
        }}
      />
    </>
  );
};
